// What the crew app reads from the server (STAGE_5). One answer per tab: the tablet is
// on LTE, and a round trip per field is a second each in a weak spot.

import { useQuery } from '@tanstack/react-query';

import { api, type Schemas } from '@/api/client';
import { ru } from '@/i18n/ru';

export const CREW_KEY = 'crew';
export const SHIFT_KEY = [CREW_KEY, 'shift'] as const;
export const CALLS_KEY = [CREW_KEY, 'calls'] as const;

export type DoctorShift = Schemas['DoctorShiftOut'];
export type CrewOnLine = Schemas['CrewOut'];
export type CrewTotals = Schemas['CrewTotalsOut'];
export type CrewCall = Schemas['DoctorCallOut'];
export type IncidentTypePick = Schemas['IncidentTypePickOut'];
export type Incident = Schemas['IncidentOut'];

export function getDoctorShift(signal?: AbortSignal): Promise<DoctorShift> {
  return api.get('/doctor/shift', signal ? { signal } : {});
}

/** The "Shift" tab's answer, shared by the tab, the top bar and "Calls". Totals move with
 * every payment and expense, so it is read again every half minute while open. */
export function useDoctorShift() {
  return useQuery({
    queryKey: SHIFT_KEY,
    queryFn: ({ signal }) => getDoctorShift(signal),
    refetchInterval: SHIFT_REFRESH_MS,
  });
}

export const SHIFT_REFRESH_MS = 30_000;

/** "3 h 12 min" on line, from the start of the shift to `now`. */
export function onLineFor(startedAt: string, now: number): string {
  const minutes = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 60_000));
  return ru.crew.duration(Math.floor(minutes / 60), minutes % 60);
}

/** "01 01A123BC" */
export function vehicleName(crew: Pick<CrewOnLine, 'vehicle_side_number' | 'vehicle_plate'>): string {
  return `${crew.vehicle_side_number} ${crew.vehicle_plate}`;
}

/** The call in hand and the crew's queue. Polled every ten seconds while on line
 * (STAGE_5 5.6 replaces it with one state answer and push). */
export const CALLS_REFRESH_MS = 10_000;

export function getDoctorCalls(signal?: AbortSignal): Promise<CrewCall[]> {
  return api.get('/doctor/calls', signal ? { signal } : {});
}

export function useDoctorCalls(enabled = true) {
  return useQuery({
    queryKey: CALLS_KEY,
    queryFn: ({ signal }) => getDoctorCalls(signal),
    refetchInterval: CALLS_REFRESH_MS,
    // A new assignment has to reach a tablet lying in the holder with the screen off.
    refetchIntervalInBackground: true,
    enabled,
  });
}

// --- "History" (TZ 8) ------------------------------------------------------------------

export type HistoryPeriod = Schemas['HistoryPeriod'];
export type DoctorHistory = Schemas['DoctorHistoryOut'];
export type HistoryCall = Schemas['DoctorHistoryCallOut'];

export const historyKey = (period: HistoryPeriod) => [CREW_KEY, 'history', period] as const;

export function getDoctorHistory(period: HistoryPeriod, signal?: AbortSignal): Promise<DoctorHistory> {
  return api.get('/doctor/history', { query: { period }, ...(signal ? { signal } : {}) });
}

/** The medic's calls of the shift or of seven days. Without a network the service worker
 * answers with the last copy (vite.config.ts, NetworkFirst), so the list is there in a
 * dead zone too; React Query keeps it for the day as well. */
export function useDoctorHistory(period: HistoryPeriod, enabled = true) {
  return useQuery({
    queryKey: historyKey(period),
    queryFn: ({ signal }) => getDoctorHistory(period, signal),
    gcTime: 24 * 60 * 60_000,
    enabled,
  });
}

/** A call of this medic's, found where the tablet has it: among the calls in hand, then
 * in the shift's history, then in seven days' - so the call card and the payment screen
 * open from "History" too. */
export function useCrewCall(callId: string | undefined): {
  call: CrewCall | undefined;
  /** Among the calls in hand; otherwise it came from "History". */
  inHand: boolean;
  loading: boolean;
} {
  const same = (item: CrewCall) => String(item.id) === callId;
  const calls = useDoctorCalls();
  const inHand = calls.data?.find(same);
  const shift = useDoctorHistory('shift', !calls.isLoading && !inHand);
  const ofShift = shift.data?.calls.find(same);
  const week = useDoctorHistory('week', !calls.isLoading && !inHand && !shift.isLoading && !ofShift);
  return {
    call: inHand ?? ofShift ?? week.data?.calls.find(same),
    inHand: inHand !== undefined,
    loading: calls.isLoading || shift.isLoading || week.isLoading,
  };
}
